/**
 * レビュー質問の優先度(#33)。
 *
 * 実データ(豚レバー)で47問出た。料理長に47問を一度に出すのは現実的でないため、
 * 「SOPが間違っているかどうか」に効く順で絞れるようにする。
 *
 * 質問そのものは CLI(buildReviewQuestions)が作る。画面は並べ替えと絞り込みだけを担う。
 * 抽出を画面側に再実装すると CLI と問いが食い違い、回答の id も合わなくなる。
 */

export type ReviewCategory =
  | "quantity"
  | "servings"
  | "temperature"
  | "taste"
  | "completion"
  | "allergen"
  | "prerequisite"
  | "disputed"
  | "unconfirmed"
  | "qhint"
  | "identify"
  | "warning";

export interface ReviewQuestion {
  id: string;
  category: ReviewCategory;
  prompt: string;
  options?: string[];
  sub?: string[];
  ref: Record<string, string | number>;
  /**
   * どの工程についての問いか(#133)。本文の各工程の下に並べるために使う。
   * 人数・アレルゲンのように工程に属さない問いは null。古い質問JSONには無い。
   */
  step_no?: number | null;
}

export type Priority = "high" | "medium" | "low";

/**
 * high  … 放置すると**間違ったSOP**になる。合議の食い違い・映像から読めなかった点・
 *         仕込みの自家製/市販・アレルゲン・自動検出の警告
 * medium… 味と再現性に効く。油温・味見・完了基準・量の具体化
 * low   … 数値の穴埋め。分量・人数(手は止まるが、間違いではない)
 */
export const PRIORITY_OF: Record<ReviewCategory, Priority> = {
  disputed: "high",
  unconfirmed: "high",
  warning: "high",
  prerequisite: "high",
  allergen: "high",
  identify: "high", // 仮名(調味料A 等)のままだと、何を入れる料理か分からないマニュアルになる(#175)
  temperature: "medium",
  taste: "medium",
  completion: "medium",
  qhint: "medium",
  quantity: "low",
  servings: "low",
};

export const PRIORITY_LABEL: Record<Priority, string> = {
  high: "高",
  medium: "中",
  low: "低",
};

export const PRIORITY_HELP: Record<Priority, string> = {
  high: "放置すると内容が誤ったまま残ります",
  medium: "味と再現性に効きます",
  low: "数値の記入です",
};

export const CATEGORY_LABEL: Record<ReviewCategory, string> = {
  quantity: "分量(材料表)",
  servings: "人数",
  temperature: "油温",
  taste: "味見の基準",
  completion: "完了基準",
  allergen: "アレルギー表示の確認",
  prerequisite: "仕込み(動画外の前工程)",
  disputed: "判断が分かれた項目",
  unconfirmed: "映像から読めなかった項目",
  qhint: "量の具体化",
  identify: "食材・調味料の特定",
  warning: "自動検出の警告",
};

export const PRIORITY_ORDER: Priority[] = ["high", "medium", "low"];

export function priorityOf(q: ReviewQuestion): Priority {
  return PRIORITY_OF[q.category] ?? "medium";
}

/** 優先度 → カテゴリ順で安定に並べる */
export function sortQuestions(qs: ReviewQuestion[]): ReviewQuestion[] {
  const rank = (q: ReviewQuestion) => PRIORITY_ORDER.indexOf(priorityOf(q));
  return [...qs].sort((a, b) => rank(a) - rank(b) || a.category.localeCompare(b.category));
}

/**
 * 開いたときの絞り込み。質問がある優先度のうち一番高いもの、どれも無ければ「すべて」。
 *
 * 常に「高」で開くと、残りが中・低だけの品目で「該当する質問はありません」になり、
 * 残っている質問が見つからない(揚げマナガツオ: 残り1問が優先度中で、画面上は0件に見えた)。
 */
export function initialFilter(qs: ReviewQuestion[]): Priority | "all" {
  const counts = countByPriority(qs);
  return PRIORITY_ORDER.find((p) => counts[p] > 0) ?? "all";
}

export function countByPriority(qs: ReviewQuestion[]): Record<Priority, number> {
  const out: Record<Priority, number> = { high: 0, medium: 0, low: 0 };
  for (const q of qs) out[priorityOf(q)] += 1;
  return out;
}

/**
 * 質問 id を Firestore のドキュメント ID に落とす。
 *
 * 質問 id には材料名や仕込み名がそのまま入る(`prereq:豚レバー` など)。
 * Firestore のドキュメント ID は `/` を含められず、`.`・`..`・`__…__` も使えない。
 * 素通しすると、その質問だけ保存時に例外になって**回答が記録できない**。
 * 変換は一方向だが、resolve へ渡すのは保存済みドキュメント内の questionId なので、
 * ID を戻す必要はない。
 */
export function answerDocId(questionId: string): string {
  const safe = questionId.replace(/\//g, "／");
  if (safe === "." || safe === "..") return `q-${safe.length}`;
  if (/^__.*__$/.test(safe)) return `q-${safe}`;
  // Firestore の上限は 1500 バイト。日本語込みでも余裕を持って切る
  return safe.length > 400 ? safe.slice(0, 400) : safe;
}

/**
 * 工程の削除依頼(#174)の文書ID。工程は動画内の時刻(start/end)で同定する。
 * step_no は反映のたびに振り直されるので鍵にしない。CLI(src/core/step-removal.ts)と同じ形。
 */
export function stepRemovalKey(step: { start: string; end: string }): string {
  return `${step.start}_${step.end}`;
}

/** 材料表の追加・削除依頼(#194)の文書ID。op と食材名から。Firestore が使えない文字は丸める */
export function ingredientEditKey(op: "add" | "remove", name: string): string {
  return answerDocId(`${op}:${name.trim()}`);
}

/**
 * 材料・調味料・分量の質問は工程に散らさず、材料表として1箇所にまとめる(柴田 2026-09-08)。
 * 分量・仮名の実名・人数は表の入力欄に、アレルゲン・仕込み・食材の食い違いは表の下に置く。
 * それ以外(油温・味見・完了基準・手順の食い違い・未確認など)は工程ごとに聞く。
 */
const MATERIAL_CATEGORIES = new Set(["quantity", "identify", "servings", "allergen", "prerequisite"]);
export function isMaterialQuestion(q: ReviewQuestion): boolean {
  if (MATERIAL_CATEGORIES.has(q.category)) return true;
  return q.category === "disputed" && /ingredients/.test(String(q.ref.field ?? ""));
}

/** 質問の入力欄の場所(画面内リンクの飛び先)。質問 id には記号が入るので安全な形にする */
export function questionAnchorId(questionId: string): string {
  return `q-${encodeURIComponent(questionId).replace(/%/g, "_")}`;
}

/** 残りの確認事項の1行。どこにあるか(材料表/工程N/全体)と、何を聞いているか */
export interface RemainingItem {
  id: string;
  /** 置き場所の種類。表示の言葉は画面側で決める */
  where: "materials" | "step" | "global";
  stepNo: number | null;
  /** 質問文から記入欄(「: ______」)を落として短くしたもの */
  text: string;
  /** 空欄のまま保存した(保留) */
  held: boolean;
  /** 答えはあるが、分量・人数として読めなかった(#115。書き直しが要る) */
  unreadable: boolean;
}

/**
 * まだ答えが入っていない確認事項(未回答と保留)と、読めなかった回答を、画面の先頭の一覧用に並べる。
 *
 * 質問は材料表・各工程・全体の確認事項に散っているため、残りが1〜2問になると
 * どこにあるか分からなくなる(揚げマナガツオ: 工程7、切干大根: 材料表の仮名)。
 * 何を答えればよいかと置き場所を先頭にまとめ、そこへ移動できるようにする。
 */
export function remainingOf(
  questions: ReviewQuestion[],
  answers: Record<string, { answer?: string | null; unreadable?: boolean } | undefined>,
  stepNos: ReadonlySet<number>,
): RemainingItem[] {
  return sortQuestions(questions)
    // 読めなかった回答(#115)は答えが入っていても残りに数える。反映しても質問が消えないため
    .filter((q) => !answers[q.id]?.answer?.trim() || answers[q.id]?.unreadable === true)
    .map((q) => {
      const where: RemainingItem["where"] = isMaterialQuestion(q)
        ? "materials"
        : q.step_no != null && stepNos.has(q.step_no)
          ? "step"
          : "global";
      return {
        id: q.id,
        where,
        stepNo: where === "step" ? q.step_no ?? null : null,
        text: shortPrompt(q.prompt),
        held: answers[q.id] != null && !answers[q.id]?.answer?.trim(),
        unreadable: !!answers[q.id]?.answer?.trim() && answers[q.id]?.unreadable === true,
      };
    });
}

function shortPrompt(prompt: string): string {
  const s = prompt.replace(/[:：]\s*_{2,}\s*$/, "").replace(/\s+/g, " ").trim();
  return s.length > 90 ? `${s.slice(0, 90)}…` : s;
}
